/**
 * TaskDescriptionCard Component
 *
 * Displays the task description (initial comment) in a dedicated card
 * above the tabs. Shows max 3-4 lines with "Show more" expansion.
 *
 * Features:
 * - Collapsible description content
 * - Attachments display (files, images, code snippets)
 * - Creator info and timestamp
 * - Tinted background matching first-comment styling
 *
 * Accessibility:
 * - ARIA expanded state for collapsible content
 * - Focus management for Show more/less button
 */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Typography, Button } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { CopyBlock } from 'react-code-blocks';
import { S3Image } from '../../../CampaignLauncher/components';
import S3File from '../../../../shared/S3File';
import { codeBlock } from '../../utils/task.utils';
import '../../styles/index.scss';

const { Text } = Typography;

// Max height matches CSS max-height (150px)
const CONTENT_MAX_HEIGHT = 150;

/**
 * Parse markdown-like content for display
 */
const parseMarkdown = (text) => {
  const elements = [];
  const lines = text.split('\n');
  let currentList = [];

  const processInline = (content) => {
    const parts = [];
    const regex = /(\*\*)(.*?)\1|(\*)(.*?)\3|(~~)(.*?)\5|(`)(.*?)\7|(https?:\/\/\S+)|(.)/gs;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(content)) !== null) {
      const [
        full, // eslint-disable-line no-unused-vars
        boldOpen,
        boldContent,
        italicOpen,
        italicContent,
        strikeOpen,
        strikeContent,
        codeOpen,
        codeContent,
        url,
        other,
      ] = match;

      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }

      if (boldOpen) {
        parts.push(<strong key={`bold-${match.index}`}>{boldContent}</strong>);
      } else if (italicOpen) {
        parts.push(<em key={`italic-${match.index}`}>{italicContent}</em>);
      } else if (strikeOpen) {
        parts.push(<del key={`strike-${match.index}`}>{strikeContent}</del>);
      } else if (codeOpen) {
        parts.push(<code key={`code-${match.index}`}>{codeContent}</code>);
      } else if (url) {
        parts.push(
          <a key={`link-${match.index}`} href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        );
      } else {
        parts.push(other);
      }

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts;
  };

  lines.forEach((line, index) => {
    if (line.startsWith('- ')) {
      const content = line.substring(2);
      currentList.push(<li key={`li-${index}`}>{processInline(content)}</li>);
    } else {
      if (currentList.length > 0) {
        elements.push(<ul key={`ul-${index}`}>{currentList}</ul>);
        currentList = [];
      }
      if (line.trim()) {
        elements.push(<span key={`line-${index}`}>{processInline(line)}</span>);
      }
      if (index !== lines.length - 1) {
        elements.push(<br key={`br-${index}`} />);
      }
    }
  });

  if (currentList.length > 0) {
    elements.push(<ul key="ul-end">{currentList}</ul>);
  }

  return elements;
};

const TaskDescriptionCard = ({
  taskDetails,
  attachedFiles,
  attachedImages,
  codeSnippet,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const contentRef = useRef(null);

  // Check if content overflows and needs expansion toggle
  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const { scrollHeight } = contentRef.current;
        setNeedsExpansion(scrollHeight > CONTENT_MAX_HEIGHT);
      }
    };

    // Check on mount and when content changes
    checkOverflow();

    // Also check after images load
    const timer = setTimeout(checkOverflow, 500);
    return () => clearTimeout(timer);
  }, [taskDetails, attachedFiles, attachedImages, codeSnippet]);

  // Check if content exists
  const hasDescription = taskDetails && taskDetails.trim();
  const hasAttachments = (attachedFiles?.length > 0) || (attachedImages?.length > 0);
  const hasCodeSnippet = typeof codeSnippet === 'string' && codeSnippet.trim();
  const hasContent = hasDescription || hasAttachments || hasCodeSnippet;

  if (!hasContent) {
    return (
      <div className="task-description-card">
        <div className="task-description-card__header">
          <Text strong className="task-description-card__title">DESCRIPTION</Text>
        </div>
        <div className="task-description-card__content">
          <Text className="task-description-card__empty">No task details available</Text>
        </div>
      </div>
    );
  }

  // Render description content with code blocks
  const renderDescription = () => {
    if (!hasDescription) return null;

    const processedParts = codeBlock(taskDetails);

    return processedParts.map((part, index) => {
      if (part.type === 'text') {
        return <span key={`text-${index}`}>{parseMarkdown(part.content)}</span>;
      }

      return (
        <pre key={`code-${index}`} className="task-description-card__code-block">
          <code>{part.content}</code>
        </pre>
      );
    });
  };

  return (
    <div className="task-description-card">
      <div className="task-description-card__header">
        <Text strong className="task-description-card__title">DESCRIPTION</Text>
        {needsExpansion && (
          <Button
            type="link"
            onClick={() => setIsExpanded(!isExpanded)}
            className="task-description-card__toggle"
            aria-expanded={isExpanded}
            aria-controls="description-content"
          >
            {isExpanded ? (
              <>Show less <UpOutlined /></>
            ) : (
              <>Show more <DownOutlined /></>
            )}
          </Button>
        )}
      </div>

      <div
        id="description-content"
        ref={contentRef}
        className={`task-description-card__content ${isExpanded ? 'task-description-card__content--expanded' : ''} ${!needsExpansion ? 'task-description-card__content--no-overflow' : ''}`}
      >
        {/* Task description text */}
        {hasDescription && (
          <div className="task-description-card__text">
            {renderDescription()}
          </div>
        )}

        {/* Attached files */}
        {attachedFiles?.length > 0 && (
          <Row className="task-description-card__attachments">
            {attachedFiles.map((file, index) => (
              <S3File key={index} file={file} />
            ))}
          </Row>
        )}

        {/* Attached images */}
        {attachedImages?.length > 0 && (
          <div className="task-description-card__images">
            {attachedImages.map((file, index) => (
              <S3Image
                key={index}
                imgKey={file.key}
                height="25%"
                width="25%"
                className="task-drawer__attachment-thumb"
              />
            ))}
          </div>
        )}

        {/* Code snippet */}
        {hasCodeSnippet && (
          <Col className="task-description-card__code-snippet">
            <CopyBlock
              text={codeSnippet}
              language="javascript"
              wrapLongLines
              showLineNumbers={false}
              theme="dracula"
              codeBlock
            />
          </Col>
        )}
      </div>
    </div>
  );
};

TaskDescriptionCard.propTypes = {
  taskDetails: PropTypes.string,
  attachedFiles: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      filename: PropTypes.string,
    })
  ),
  attachedImages: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
    })
  ),
  codeSnippet: PropTypes.string,
};

TaskDescriptionCard.defaultProps = {
  taskDetails: '',
  attachedFiles: [],
  attachedImages: [],
  codeSnippet: '',
};

export default TaskDescriptionCard;
